import { useState, useEffect, useCallback } from 'react';
import { 
  Truck, Store, MapPin, Globe, Map, Plus, Trash2, Search, 
  Check, X, AlertCircle, ChevronRight, RefreshCw, ShoppingCart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeliveryZonesPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Stores and States
  const [ecommerceStores, setEcommerceStores] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  
  // Store's current zones
  const [storeZones, setStoreZones] = useState([]);
  
  // Add zones dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedStateForAdd, setSelectedStateForAdd] = useState('');
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [savingZones, setSavingZones] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState('zone'); // 'zone' or 'state'
  
  // Pincode lookup
  const [pincodeLookup, setPincodeLookup] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  const getToken = () => localStorage.getItem('access_token');

  // Fetch e-commerce enabled stores
  const fetchEcommerceStores = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations/ecommerce-stores`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setEcommerceStores(data);
      if (data.length > 0 && !selectedStore) {
        setSelectedStore(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch e-commerce stores:', error);
    }
  }, [selectedStore]);

  // Fetch states
  const fetchStates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations/states/dropdown`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  }, []);

  // Fetch store's delivery zones
  const fetchStoreZones = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/locations/delivery-zones/by-store/${selectedStore.id}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setStoreZones(data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch delivery zones', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedStore, toast]);

  // Fetch available districts for a state
  const fetchAvailableDistricts = useCallback(async (stateId) => {
    if (!stateId) return;
    try {
      const response = await fetch(`${API_URL}/api/locations/delivery-zones/available-districts?state_id=${stateId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setAvailableDistricts(data);
    } catch (error) {
      console.error('Failed to fetch available districts:', error);
    }
  }, []);

  // Pincode lookup
  const handlePincodeLookup = async () => {
    if (!pincodeLookup || pincodeLookup.length !== 6) {
      toast({ title: 'Error', description: 'Please enter a valid 6-digit pincode', variant: 'destructive' });
      return;
    }
    
    setLookingUp(true);
    try {
      const response = await fetch(`${API_URL}/api/locations/delivery-zones/check-pincode/${pincodeLookup}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      setLookupResult(data);
    } catch (error) {
      setLookupResult({ is_deliverable: false, message: 'Error looking up pincode' });
    } finally {
      setLookingUp(false);
    }
  };

  // Save new zone mappings
  const handleSaveZones = async () => {
    if (!selectedStore || !selectedStateForAdd || selectedDistricts.length === 0) {
      toast({ title: 'Error', description: 'Please select districts to map', variant: 'destructive' });
      return;
    }

    setSavingZones(true);
    try {
      const response = await fetch(`${API_URL}/api/locations/delivery-zones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          store_id: selectedStore.id,
          state_id: selectedStateForAdd,
          district_ids: selectedDistricts
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create mappings');
      }

      toast({ 
        title: 'Success', 
        description: `Mapped ${data.created.length} districts. ${data.skipped.length > 0 ? `Skipped ${data.skipped.length} already mapped.` : ''}`
      });
      
      setAddDialogOpen(false);
      setSelectedDistricts([]);
      setSelectedStateForAdd('');
      fetchStoreZones();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSavingZones(false);
    }
  };

  // Delete zone or state mapping
  const handleDelete = async () => {
    try {
      let url;
      if (deleteType === 'zone') {
        url = `${API_URL}/api/locations/delivery-zones/${deleteItem.id}`;
      } else {
        url = `${API_URL}/api/locations/delivery-zones/store/${selectedStore.id}/state/${deleteItem.state_id}`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      toast({ title: 'Success', description: deleteType === 'zone' ? 'District unmapped successfully' : 'State mapping removed' });
      setDeleteDialogOpen(false);
      fetchStoreZones();
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Toggle district selection
  const toggleDistrict = (districtId) => {
    setSelectedDistricts(prev => 
      prev.includes(districtId) 
        ? prev.filter(id => id !== districtId)
        : [...prev, districtId]
    );
  };

  // Select all available districts
  const selectAllAvailable = () => {
    const available = availableDistricts.filter(d => !d.is_mapped).map(d => d.id);
    setSelectedDistricts(available);
  };

  // Initial load
  useEffect(() => {
    fetchEcommerceStores();
    fetchStates();
  }, [fetchEcommerceStores, fetchStates]);

  // Load zones when store changes
  useEffect(() => {
    if (selectedStore) {
      fetchStoreZones();
    }
  }, [selectedStore, fetchStoreZones]);

  // Load districts when state changes in dialog
  useEffect(() => {
    if (selectedStateForAdd) {
      fetchAvailableDistricts(selectedStateForAdd);
      setSelectedDistricts([]);
    }
  }, [selectedStateForAdd, fetchAvailableDistricts]);

  const totalDistricts = storeZones.reduce((acc, state) => acc + state.districts.length, 0);

  return (
    <div className="p-6 space-y-6" data-testid="delivery-zones-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Delivery Zones</h1>
          <p className="text-gray-500 text-sm">Map districts to stores for E-Commerce delivery</p>
        </div>
      </div>

      {/* No E-Commerce Stores Warning */}
      {ecommerceStores.length === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
              <div>
                <h3 className="font-semibold text-amber-800">No E-Commerce Enabled Stores</h3>
                <p className="text-amber-700 mt-1">
                  To map delivery zones, first enable E-Commerce for at least one store. 
                  Go to <strong>Organization → Stores</strong>, edit a store, and enable the E-Commerce toggle in the E-Commerce tab.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {ecommerceStores.length > 0 && (
        <>
          {/* Store Selector & Pincode Lookup */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <Label className="text-gray-700 font-medium">Select Store</Label>
                <Select 
                  value={selectedStore?.id || ''} 
                  onValueChange={(v) => setSelectedStore(ecommerceStores.find(s => s.id === v))}
                >
                  <SelectTrigger className="mt-2 bg-gray-50 border-gray-300">
                    <SelectValue placeholder="Select a store" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {ecommerceStores.map(store => (
                      <SelectItem key={store.id} value={store.id}>
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4 text-indigo-500" />
                          {store.name} ({store.code})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-200">
              <CardContent className="p-4">
                <Label className="text-gray-700 font-medium">Test Pincode Delivery</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Enter 6-digit pincode"
                    value={pincodeLookup}
                    onChange={(e) => { setPincodeLookup(e.target.value); setLookupResult(null); }}
                    maxLength={6}
                    className="bg-gray-50 border-gray-300"
                  />
                  <Button onClick={handlePincodeLookup} disabled={lookingUp} className="bg-indigo-600 hover:bg-indigo-700">
                    {lookingUp ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                {lookupResult && (
                  <div className={`mt-3 p-3 rounded-lg ${lookupResult.is_deliverable ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2">
                      {lookupResult.is_deliverable ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <X className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-medium ${lookupResult.is_deliverable ? 'text-green-700' : 'text-red-700'}`}>
                        {lookupResult.message}
                      </span>
                    </div>
                    {lookupResult.area_name && (
                      <p className="text-sm text-gray-600 mt-1">
                        {lookupResult.area_name}, {lookupResult.city_name}, {lookupResult.district_name}, {lookupResult.state_name}
                      </p>
                    )}
                    {lookupResult.store_name && (
                      <p className="text-sm text-indigo-600 mt-1">
                        <Store className="w-3 h-3 inline mr-1" /> Served by: {lookupResult.store_name}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary & Add Button */}
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="p-3 bg-white/20 rounded-lg">
                    <Truck className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Delivery Coverage for {selectedStore?.name}</p>
                    <div className="flex items-center gap-6 mt-2">
                      <div>
                        <p className="text-3xl font-bold">{storeZones.length}</p>
                        <p className="text-indigo-200 text-sm">States</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold">{totalDistricts}</p>
                        <p className="text-indigo-200 text-sm">Districts</p>
                      </div>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={() => setAddDialogOpen(true)} 
                  className="bg-white text-indigo-600 hover:bg-indigo-50"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Delivery Zone
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Zones List */}
          <div className="space-y-4">
            {loading ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-12 text-center">
                  <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto" />
                  <p className="text-gray-500 mt-2">Loading zones...</p>
                </CardContent>
              </Card>
            ) : storeZones.length === 0 ? (
              <Card className="bg-white border-gray-200">
                <CardContent className="py-12 text-center">
                  <Map className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No delivery zones mapped yet</p>
                  <p className="text-sm text-gray-400 mt-1">Click "Add Delivery Zone" to map districts to this store</p>
                </CardContent>
              </Card>
            ) : (
              storeZones.map(stateZone => (
                <Card key={stateZone.state_id} className="bg-white border-gray-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-indigo-600" />
                        <CardTitle className="text-lg text-gray-800">{stateZone.state_name}</CardTitle>
                        <Badge className="bg-indigo-100 text-indigo-700">{stateZone.districts.length} Districts</Badge>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setDeleteItem(stateZone); setDeleteType('state'); setDeleteDialogOpen(true); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Remove All
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {stateZone.districts.map(district => (
                        <Badge 
                          key={district.id} 
                          variant="outline" 
                          className="py-1.5 px-3 border-gray-300 text-gray-700 hover:bg-red-50 hover:border-red-300 cursor-pointer group"
                          onClick={() => { setDeleteItem(district); setDeleteType('zone'); setDeleteDialogOpen(true); }}
                        >
                          <MapPin className="w-3 h-3 mr-1 text-gray-400 group-hover:text-red-500" />
                          {district.district_name}
                          <X className="w-3 h-3 ml-2 text-gray-400 group-hover:text-red-500" />
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {/* Add Zone Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />
              Add Delivery Zone
            </DialogTitle>
            <DialogDescription>
              Map districts to {selectedStore?.name} for E-Commerce delivery
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* State Selection */}
            <div className="space-y-2">
              <Label>Select State</Label>
              <Select value={selectedStateForAdd} onValueChange={setSelectedStateForAdd}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="Select a state" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {states.map(state => (
                    <SelectItem key={state.id} value={state.id}>
                      {state.name} ({state.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Districts Selection */}
            {selectedStateForAdd && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Select Districts</Label>
                  <Button variant="link" size="sm" onClick={selectAllAvailable} className="text-indigo-600">
                    Select All Available
                  </Button>
                </div>
                
                {availableDistricts.length === 0 ? (
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-gray-500">No districts found for this state</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-10"></TableHead>
                          <TableHead>District</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableDistricts.map(district => (
                          <TableRow 
                            key={district.id} 
                            className={`cursor-pointer ${district.is_mapped ? 'bg-gray-50 opacity-60' : 'hover:bg-indigo-50'}`}
                            onClick={() => !district.is_mapped && toggleDistrict(district.id)}
                          >
                            <TableCell>
                              <Checkbox 
                                checked={selectedDistricts.includes(district.id)}
                                disabled={district.is_mapped}
                                onCheckedChange={() => toggleDistrict(district.id)}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-gray-800">
                              {district.name}
                            </TableCell>
                            <TableCell>
                              {district.is_mapped ? (
                                <Badge className="bg-amber-100 text-amber-700">
                                  Mapped to {district.mapped_to_store}
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700">Available</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {selectedDistricts.length > 0 && (
                  <p className="text-sm text-indigo-600">
                    {selectedDistricts.length} district(s) selected
                  </p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveZones} 
              disabled={savingZones || selectedDistricts.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingZones ? 'Mapping...' : `Map ${selectedDistricts.length} District(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Removal</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === 'zone' 
                ? `Are you sure you want to remove "${deleteItem?.district_name}" from the delivery zone?`
                : `Are you sure you want to remove all districts in "${deleteItem?.state_name}" from the delivery zone?`
              }
              <br /><br />
              Customers in this area will no longer be able to place orders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DeliveryZonesPage;
